import { type CompanyWithUI } from "@/components/shared/CompanyCardHorizontal";

const ensureArray = (value: unknown): any[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

export type DiscoverShortlistContext = {
  isAuthenticated: boolean;
  guestShortlistedIds: Set<string>;
  shortlistedIds: Set<string>;
};

export function resolveDiscoverIsShortlisted(
  companyId: string,
  { isAuthenticated, guestShortlistedIds, shortlistedIds }: DiscoverShortlistContext,
): boolean {
  return isAuthenticated ? shortlistedIds.has(companyId) : guestShortlistedIds.has(companyId);
}

export const mapListCompanies = (
  companies: any[],
  shortlistContext: DiscoverShortlistContext,
): CompanyWithUI[] =>
  (companies || []).map((item: any) => ({
    ...item,
    isShortlisted: resolveDiscoverIsShortlisted(item.id, shortlistContext),
  })) as CompanyWithUI[];

export const mapAiResultsToCompanies = (
  results: any[],
  shortlistContext: DiscoverShortlistContext,
): CompanyWithUI[] =>
  results.map((item: any) => {
    const companyId = item.company_id || item.id;

    return {
      id: companyId,
      gtid: item.gtid,
      legalName: item.legal_name || item.legalName,
      tradeName: item.trade_name || item.tradeName,
      registrationNumber: item.registration_number || item.registrationNumber,
      yearOfIncorporation: item.year_of_incorporation || item.yearOfIncorporation,
      website: item.company_website || item.website,
      email: item.director_email || item.email,
      phoneCountryCode: item.phone_country_code || item.phoneCountryCode,
      phoneNumber: item.phone_number || item.phoneNumber,
      description: item.description,
      subDescription: item.subDescription || null,
      customerOfferHighlight: ensureArray(
        item.customer_offer_highlight ?? item.customerOfferHighlight,
      ),
      industryCategory: ensureArray(item.industry_category || item.industryCategory),
      subIndustry: ensureArray(item.sub_industry || item.subIndustry),
      collaborationIntent: ensureArray(item.collaboration_intent || item.collaborationIntent),
      coreCapabilities: ensureArray(item.core_capabilities || item.coreCapabilities),
      secondaryCapabilities: ensureArray(item.secondary_capabilities || item.secondaryCapabilities),
      keyDifferentiators: item.key_differentiators || item.keyDifferentiators,
      technologies: ensureArray(item.technologies_infrastructure || item.technologies),
      headquartersCountry: item.country || item.headquartersCountry,
      headquartersCity: item.state || item.headquartersCity,
      currentAddress: item.headquarters_location || item.currentAddress,
      operatingCountries: ensureArray(item.operating_countries || item.operatingCountries),
      preferredCollaborationRegions: ensureArray(item.preferred_collaboration_regions || item.preferredCollaborationRegions),
      status: item.status || "VERIFIED",
      isShortlisted: resolveDiscoverIsShortlisted(companyId, shortlistContext),
    } as unknown as CompanyWithUI;
  });

export const extractAiSearchResults = (response: unknown): any[] => {
  const responseData = response as Record<string, unknown>;
  const nestedResult =
    "result" in responseData &&
      responseData.result &&
      typeof responseData.result === "object"
      ? (responseData.result as { data?: { json?: { results?: unknown[] } } }).data?.json?.results
      : undefined;
  const flatResults =
    "results" in responseData && Array.isArray(responseData.results) ? responseData.results : undefined;
  const companyResults =
    "companies" in responseData && Array.isArray(responseData.companies) ? responseData.companies : undefined;
  return (nestedResult ?? flatResults ?? companyResults ?? []) as any[];
};

export const mapQuickSearchCompanies = (
  companies: any[],
  shortlistContext: DiscoverShortlistContext,
): CompanyWithUI[] =>
  (companies || []).map(
    (item: any) => {
      const companyId = item.companyId || item.id;
      return {
        id: companyId,
        gtid: item.gtid || "",
        legalName: "",
        tradeName: null,
        registrationNumber: null,
        yearOfIncorporation: null,
        website: null,
        email: null,
        phoneCountryCode: null,
        phoneNumber: null,
        description: item.description || null,
        subDescription: item.subDescription || null,
        customerOfferHighlight: ensureArray(item.customerOfferHighlight),
        industryCategory: ensureArray(item.industryCategory),
        subIndustry: ensureArray(item.subIndustry),
        collaborationIntent: ensureArray(item.collaborationIntent),
        coreCapabilities: ensureArray(item.coreCapabilities),
        secondaryCapabilities: [],
        keyDifferentiators: null,
        technologies: ensureArray(item.technologies),
        headquartersCountry: item.headquartersCountry || "",
        headquartersCity: item.headquartersCity || "",
        currentAddress: null,
        operatingCountries: [],
        preferredCollaborationRegions: [],
        status: item.status || "VERIFIED",
        isShortlisted: resolveDiscoverIsShortlisted(companyId, shortlistContext),
      } as unknown as CompanyWithUI;
    },
  );
